import api from "./api";
import { INDICATIONS_TYPES } from "./constants";
import { isValidMongoId } from "@/utils/validators";

const indicationService = {};
const endpoint = "/indications";

const normalizeCreateIndicationErrorMessage = (error) => {
  const rawMessage =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    (typeof error?.response?.data === "string" ? error.response.data : null) ||
    error?.message ||
    null;

  if (typeof rawMessage !== "string") return null;

  return rawMessage
    .trim()
    .replace(/^error\s*:\s*/i, "")
    .replace(/^error\s+/i, "")
    .trim();
};

indicationService.createIndication = function (indication, options = {}) {
  const requestOptions = {
    ...options,
    getErrorMessage:
      typeof options.getErrorMessage === "function"
        ? options.getErrorMessage
        : normalizeCreateIndicationErrorMessage,
  };

  return api
    .post(endpoint, indication, requestOptions)
    .then(({ data }) => data);
};

indicationService.get = function (patientId, indicationsFilter) {
  const filters = getIndicationTypes(indicationsFilter);
  return api
    .get(endpoint, {
      params: { patientId, indicationTypeFilter: filters },
    })
    .then((res) => res.data);
};

indicationService.getByAppointment = function (patientId, appointmentId) {
  return api
    .get(endpoint, {
      params: { patientId, appointmentId },
    })
    .then((res) => res.data);
};

indicationService.getById = function (indicationId) {
  return api.get(`${endpoint}/${indicationId}`).then(({ data }) => data);
};

indicationService.getMedicationHistory = function ({
  patient,
  from,
  to,
  offset,
  limit,
} = {}) {
  return api
    .get(`${endpoint}/medications/history`, {
      params: { patient, from, to, offset, limit },
    })
    .then(({ data }) => data);
};

indicationService.getLabHistory = function ({
  patient,
  from,
  to,
  offset,
  limit,
} = {}) {
  return api
    .get(`${endpoint}/lab/history`, {
      params: { patient, from, to, offset, limit },
    })
    .then(({ data }) => data);
};

indicationService.getImagesHistory = function ({
  patient,
  from,
  to,
  offset,
  limit,
} = {}) {
  return api
    .get(`${endpoint}/images/history`, {
      params: { patient, from, to, offset, limit },
    })
    .then(({ data }) => data);
};

indicationService.update = function (indicationId, payload) {
  return api
    .put(`${endpoint}/${indicationId}/edit`, payload)
    .then(({ data }) => data);
};

indicationService.resendEmail = function (indicationId, email) {
  return api
    .post(`${endpoint}/${indicationId}/resend-email`, { email })
    .then(({ data }) => data);
};

indicationService.generatePdf = function (indicationId) {
  return api
    .post(`${endpoint}/${indicationId}/generate-pdf`)
    .then(({ data }) => data);
};

// Método reutilizable para guardar indicaciones - versión completa
indicationService.saveIndications = async function (
  savedIndications,
  patientId,
  appointmentId,
  typeFilter = null,
  store = null,
  options = {}
) {
  // El bloqueo contra el doble envío vive en el componente que llama, no acá:
  // tiene que levantarse de forma sincrónica antes del primer await, y para
  // cuando se entra a este servicio ya es tarde.
  const { onTriggerUpload = null, onUpdateIndication = null } = options;

  let pendingIndications;
  if (typeFilter && typeFilter.length > 1) {
    pendingIndications = savedIndications.filter(
      (indication) =>
        !indication.createdAt &&
        (typeFilter ? typeFilter.includes(indication.type) : true)
    );
  } else {
    pendingIndications = savedIndications.filter(
      (indication) => !indication.createdAt
    );
  }

  if (pendingIndications.length === 0) {
    return [];
  }

  try {
    const createdIndications = [];

    for (const indication of pendingIndications) {
      const values = {};

      for (const key in indication.defaultValues) {
        const value = indication.defaultValues[key].value;
        const isRequired = indication.defaultValues[key].required;

        if (value || isRequired) {
          if (key === "isLongTerm") {
            values[key] = value === "Sí" ? true : false;
          } else if (key === "recetarioSolidario") {
            values[key] = value === "Sí" ? true : false;
          } else if (key === "generic") {
            values[key] = value === "Sí" ? true : false;
          } else if (key === "replaceable") {
            values[key] = value === "Sí" ? "S" : "N";
          } else if (key === "medication") {
            if (value) {
              const medication = store?.state?.cacheMedications?.[value];
              if (isValidMongoId(value)) {
                values.medication = value;
              }
              if (medication) {
                values.publicMedication = {
                  name: medication.name,
                  drugId: medication._id,
                  brandName: medication.brandName,
                  drug: medication.drug,
                  presentation: medication.presentation,
                  isPsychotropic: medication.isPsychotropic ?? false,
                };
              }
            }
          } else if (key === "diagnosis") {
            if (value) {
              const diagnosis = store?.state?.cacheDiagnoses?.[value];
              if (isValidMongoId(value)) {
                values.diagnosis = {
                  id: value,
                  name: diagnosis?.description || "",
                };
              } else if (typeof value === "object" && value.id) {
                values.diagnosis = {
                  id: value.id,
                  name: value.name || value.description || "",
                };
              }
            }
          } else if (key === "patientEmail") {
            if (value && value.trim() !== "") {
              values.patientEmail = value.trim();
            }
          } else if (key === "patientDni") {
            if (value !== undefined && value !== null) {
              values.patientDni = String(value).trim();
            }
          } else if (key === "patientSocioNumber") {
            if (value !== undefined && value !== null) {
              values.patientSocioNumber = String(value).trim();
            }
          } else if (key === "patientFinanciadorName") {
            if (value && value.trim() !== "") {
              values.patientFinanciadorName = value.trim();
            }
          } else if (key === "image") {
            const normalizedImages = Array.isArray(value)
              ? value
                  .map((imageValue) =>
                    typeof imageValue === "object"
                      ? imageValue?._id || imageValue?.id
                      : imageValue
                  )
                  .filter(Boolean)
              : [];

            values.image = normalizedImages;
          } else {
            values[key] = value;
          }
        }
      }
      const parsedIndication = {
        ...values,
        type: indication.type,
        patientId: patientId,
        appointment: appointmentId,
      };

      if (parsedIndication.type === "images") {
        const imageIds = Array.isArray(parsedIndication.image)
          ? parsedIndication.image
          : [];
        console.log("[images-order] sending indication", {
          indicationKey: indication.key,
          imageCount: imageIds.length,
          imageIds,
        });
      }

      const createdIndication = indication._backendId
        ? await this.update(indication._backendId, parsedIndication)
        : await this.createIndication(parsedIndication);

      const updatedIndication = {
        ...indication,
        _id: createdIndication._id,
        createdAt: createdIndication.createdAt,
      };

      if (indication.hasFiles && onTriggerUpload) {
        onTriggerUpload(updatedIndication);
      }

      if (onUpdateIndication) {
        onUpdateIndication(updatedIndication);
      }

      createdIndications.push(updatedIndication);
    }

    return createdIndications;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

const getIndicationTypes = (indicationsFilter) => {
  // TODO: Remove this. By default, if the don't scpecify a filter, we should return everything without providing every single type
  if (!indicationsFilter) return INDICATIONS_TYPES.map((item) => item.type);

  const indicationTypes = indicationsFilter.map(
    (indication) => indication.type
  );
  return indicationTypes;
};

export default indicationService;
